import type { ChatMessage } from './reducer';

/**
 * Client-side persistence for a conversation transcript (display-only history).
 *
 * The whole `ChatMessage[]` is plain, serializable data (text · ordered blocks ·
 * raw A2UI JSON · citations · flags), so a conversation survives a page refresh
 * via localStorage. Restored surfaces render again (the A2uiPanel re-feeds their
 * raw JSON to a fresh MessageProcessor) but are frozen read-only — see the
 * reducer `hydrate` case, which marks restored A2UI turns consumed.
 *
 * Scope caveat: this persists the *transcript* only, not the backend thread. The
 * restored surfaces are a read-only record; re-firing their actions would start a
 * new run/session, not resume the old graph state.
 */

const NS = 'lb-chat';
const VERSION = 1;

interface Envelope {
  v: number;
  messages: ChatMessage[];
}

function storageKey(key: string): string {
  return `${NS}:${key}`;
}

/** Resolve a storage backend safely (SSR / disabled localStorage → null). */
function resolveStorage(storage?: Storage): Storage | null {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null; // e.g. privacy mode throwing on access
  }
}

/** Load a persisted transcript for `key`, or null when absent/invalid. */
export function loadMessages(key: string, storage?: Storage): ChatMessage[] | null {
  const s = resolveStorage(storage);
  if (!s) return null;
  try {
    const raw = s.getItem(storageKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Envelope;
    if (!parsed || parsed.v !== VERSION || !Array.isArray(parsed.messages)) return null;
    return parsed.messages;
  } catch {
    return null;
  }
}

/** Persist a transcript for `key`. No-op when storage is unavailable. */
export function saveMessages(key: string, messages: ChatMessage[], storage?: Storage): void {
  const s = resolveStorage(storage);
  if (!s) return;
  try {
    const envelope: Envelope = { v: VERSION, messages };
    s.setItem(storageKey(key), JSON.stringify(envelope));
  } catch {
    // Quota exceeded / serialization failure — persistence is best-effort.
  }
}

/** Remove any persisted transcript for `key` (e.g. on conversation reset). */
export function clearMessages(key: string, storage?: Storage): void {
  const s = resolveStorage(storage);
  if (!s) return;
  try {
    s.removeItem(storageKey(key));
  } catch {
    // ignore
  }
}
